import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Users, Target, TrendingUp, Crown, Award } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ChallengeCard = ({ challenge, onStart, featured = false }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-blue-600 bg-blue-100';
      case 'advanced': return 'text-purple-600 bg-purple-100';
      case 'expert': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCompletionRateColor = (rate) => {
    if (rate >= 70) return 'text-green-600';
    if (rate >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`bg-card border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300 relative ${
        featured ? 'ring-2 ring-yellow-400 ring-opacity-50' : ''
      }`}
    >
      {/* Featured Badge */}
      {featured && (
        <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
          <Crown className="w-3 h-3" />
          <span>Featured</span>
        </div>
      )}
      {/* Trending Badge */}
      {challenge?.trending && !featured && (
        <div className="absolute -top-2 -right-2 bg-green-400 text-green-900 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
          <TrendingUp className="w-3 h-3" />
          <span>Trending</span>
        </div>
      )}
      {/* Live Badge */}
      {challenge?.status === 'live' && (
        <div className="absolute -top-2 -right-2 bg-red-400 text-red-900 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 animate-pulse">
          <div className="w-2 h-2 bg-red-600 rounded-full"></div>
          <span>LIVE</span>
        </div>
      )}
      {/* Challenge Icon */}
      <div className={`w-12 h-12 ${challenge?.color || 'bg-blue-500'} rounded-lg flex items-center justify-center mb-4`}>
        {challenge?.icon && React.cloneElement(challenge?.icon, { className: "w-6 h-6 text-white" })}
      </div>
      {/* Challenge Info */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2 line-clamp-2">{challenge?.title}</h3>
        <p className="text-muted-foreground text-sm mb-3 line-clamp-3">
          {challenge?.description}
        </p>

        {/* Category and Difficulty */}
        <div className="flex items-center space-x-2 mb-3">
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
            {challenge?.category}
          </span>
          <span className={`text-xs px-2 py-1 rounded font-medium ${getDifficultyColor(challenge?.difficulty)}`}>
            {challenge?.difficulty}
          </span>
        </div>

        {/* Challenge Stats */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span>{challenge?.timeLimit}min</span>
          </div>
          <div className="flex items-center space-x-2">
            <Target className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-yellow-600">{challenge?.points} pts</span>
          </div>
          <div className="flex items-center space-x-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span>{challenge?.participants?.toLocaleString()} joined</span>
          </div>
          <div className="flex items-center space-x-2">
            <Award className="w-4 h-4 text-muted-foreground" />
            <span className={getCompletionRateColor(challenge?.completionRate)}>
              {challenge?.completionRate}% success
            </span>
          </div>
        </div>

        {/* Tech Stack Tags */}
        {challenge?.techStack && (
          <div className="flex flex-wrap gap-1 mt-3">
            {challenge?.techStack?.slice(0, 3)?.map((tech, index) => (
              <span key={index} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                {tech}
              </span>
            ))}
            {challenge?.techStack?.length > 3 && (
              <span className="text-xs text-muted-foreground">
                +{challenge?.techStack?.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>
      {/* Action Button */}
      <Button
        variant={featured ? 'default' : 'outline'}
        size="sm"
        onClick={() => onStart?.(challenge?.id)}
        className="w-full"
      >
        {challenge?.status === 'live' ? 'Join Competition' : 'Start Challenge'}
      </Button>
      {/* Special Indicators */}
      {challenge?.type === 'competition' && (
        <div className="mt-2 text-center">
          <span className="text-xs text-orange-600 font-medium">
            🏆 Competition Mode
          </span>
        </div>
      )}
      {challenge?.industry && (
        <div className="mt-2 text-center">
          <span className="text-xs text-muted-foreground">
            Industry: {challenge?.industry}
          </span>
        </div>
      )}
    </motion.div>
  );
};

export default ChallengeCard;